import { defineStore } from 'pinia';
import { computed, ref } from 'vue';

import { injectStrict } from '@apps/main/modules/helpers/injectStrict';
import { RecipeStateKey } from '@apps/main/modules/state/state';
import type {
  FerryFilterEvents,
  FerryFilters,
} from '@apps/main/new-desktop/modules/ferryFilters';
import { FerryFilterType } from '@apps/main/new-desktop/modules/ferryFilters';
import {
  FerryIngredient,
  FlightIngredient,
} from '@apps/main/modules/recipe/ingredients';

import type { IngredientId } from '@libs/trip-planner-interfaces';

import { FilterType } from '../../../modules/filters';

import {
  getArrivalTimeRangeSliderFilter,
  getDefaultFilters,
  getDepartureTimeRangeSliderFilter,
  getStringRangeType,
} from './ferryFiltersStoreLogic';

export const useFerryFiltersStore = defineStore('FerryFiltersStore', () => {
  const recipeState = injectStrict(RecipeStateKey);

  //Helper getters
  const allowedFerryIngredients = computed<FerryIngredient[]>(() => {
    if (allowedFerryIngredientIds.value?.length) {
      return recipeState.ferryOnlyRecipe.value.filter((ferryIngredient) =>
        allowedFerryIngredientIds.value?.includes(ferryIngredient.ingredientId)
      );
    }
    return recipeState.ferryOnlyRecipe.value;
  });

  //Helper state

  const allowedFerryIngredientIds = ref<IngredientId[] | null>(null);

  // State

  const isPanelOpened = ref<boolean>(false);

  // Actions

  function openPanel(): void {
    isPanelOpened.value = true;
  }

  function closePanel(): void {
    isPanelOpened.value = false;
  }

  function update(action: FerryFilterEvents): void {
    switch (action.filterType) {
      case FerryFilterType.ArrivalTime: {
        onUpdateArrivalTimeFilter(action);
        break;
      }
      case FerryFilterType.DepartureTime: {
        onUpdateDepartureTimeFilter(action);
        break;
      }
    }
  }

  function updateAll(actions: FerryFilterEvents[]): void {
    actions.forEach((action: FerryFilterEvents) => update(action));
  }

  function resetAllFilters(): void {
    allowedFerryIngredientIds.value?.forEach((ingerdientId) => {
      const ferryIngredient = recipeState.getFerryIngredient(ingerdientId);

      ferryIngredient.filters = FerryIngredient.defaultFilters;
    });
  }

  function setAllowedFerryIngredientIds(ingredientIds: IngredientId[]): void {
    allowedFerryIngredientIds.value = ingredientIds;
  }

  function clearAllowedFerryIngredientIds(): void {
    allowedFerryIngredientIds.value = null;
  }

  function removeFilter(tagId: string, ingredientId: IngredientId): void {
    const ferryIngredient = recipeState.getFerryIngredient(ingredientId);
    ferryIngredient.filters[tagId] = FlightIngredient.defaultFilters[tagId];
  }

  // Helper functions

  function onUpdateArrivalTimeFilter(action: FerryFilterEvents): void {
    if (action.data.filterType === FilterType.RangeSlider) {
      const ingredient = recipeState.getFerryIngredient(
        action.data.ingredientId
      );
      ingredient.filters.arrivalTimeFilters = getStringRangeType(
        action.data.range
      );
    }
  }

  function onUpdateDepartureTimeFilter(action: FerryFilterEvents): void {
    if (action.data.filterType === FilterType.RangeSlider) {
      const ingredient = recipeState.getFerryIngredient(
        action.data.ingredientId
      );

      ingredient.filters.departureTimeFilters = getStringRangeType(
        action.data.range
      );
    }
  }

  // Getters

  const filters = computed<FerryFilters | null>(() => {
    if (allowedFerryIngredients.value?.length === 0) {
      return null;
    }

    const newFilters: FerryFilters = getDefaultFilters();

    allowedFerryIngredients.value.forEach((ferry) => {
      if (ferry) {
        newFilters.arrivalTime.filters.push(
          getArrivalTimeRangeSliderFilter(ferry)
        );

        newFilters.departureTime.filters.push(
          getDepartureTimeRangeSliderFilter(ferry)
        );
      }
    });

    return newFilters;
  });

  return {
    // State
    isPanelOpened,

    // Actions
    closePanel,
    openPanel,
    update,
    updateAll,
    resetAllFilters,
    removeFilter,
    setAllowedFerryIngredientIds,
    clearAllowedFerryIngredientIds,

    // Getters
    filters,
  };
});
